import React, { useState, useRef } from 'react';
import { WallPost, wallPostsApi } from '../../../api/wallPostsApi';
import { compressImageFile } from '../../../utils/imageCompression';
import { getPostImageUrls } from '../../../utils/avatarUtils';
import { BottomSheet, StatusActionButton } from '../../../shared/ui';
import styles from './EditPostModal.module.css';

interface EditPostModalProps {
  post: WallPost;
  isOpen: boolean;
  onClose: () => void;
  onUpdated: (post: WallPost) => void;
}

const MAX_PHOTOS = 10;

const EditPostModal: React.FC<EditPostModalProps> = ({ post, isOpen, onClose, onUpdated }) => {
  const existingImages = getPostImageUrls(post.images ?? []);

  // Существующие фото: {mediaId, thumbUrl, fullUrl} — можно удалить или сделать главным
  const [existingPhotos, setExistingPhotos] = useState(() =>
    existingImages.map((img, i) => ({
      mediaId: post.images![i]?.id as number,
      thumbUrl: img.thumbUrl,
      removed: false,
    }))
  );
  const [coverMediaId, setCoverMediaId] = useState<number | null>(
    existingImages.length > 0 ? (post.images![0]?.id as number) : null
  );

  // Новые фото
  const [newFiles, setNewFiles] = useState<File[]>([]);
  const [newPreviews, setNewPreviews] = useState<string[]>([]);

  const [content, setContent] = useState(post.content);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadStage, setUploadStage] = useState<'compressing' | 'uploading'>('compressing');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const simulationRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const activeExisting = existingPhotos.filter(p => !p.removed);
  const totalPhotos = activeExisting.length + newFiles.length;
  const canAddMore = totalPhotos < MAX_PHOTOS;

  const handleRemoveExisting = (mediaId: number) => {
    setExistingPhotos(prev => prev.map(p => p.mediaId === mediaId ? { ...p, removed: true } : p));
    if (coverMediaId === mediaId) {
      const next = existingPhotos.find(p => !p.removed && p.mediaId !== mediaId);
      setCoverMediaId(next?.mediaId ?? (newFiles.length > 0 ? -1 : null));
    }
  };

  const handleRemoveNew = (index: number) => {
    setNewFiles(prev => prev.filter((_, i) => i !== index));
    setNewPreviews(prev => prev.filter((_, i) => i !== index));
    if (coverMediaId === -(index + 1)) setCoverMediaId(activeExisting[0]?.mediaId ?? null);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []).filter(f => f.size > 0 && f.type.startsWith('image/'));
    if (!files.length) return;
    const toAdd = files.slice(0, MAX_PHOTOS - totalPhotos);
    setNewFiles(prev => [...prev, ...toAdd]);
    toAdd.forEach(f => {
      const url = URL.createObjectURL(f);
      setNewPreviews(prev => [...prev, url]);
    });
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleSubmit = async () => {
    if (isSubmitting) return;
    setError(null);
    setIsSubmitting(true);
    setUploadStage('compressing');
    setUploadProgress(0);

    let simInterval: ReturnType<typeof setInterval> | null = null;

    try {
      // Сжатие новых фото
      const compressed: File[] = [];
      for (let i = 0; i < newFiles.length; i++) {
        setUploadProgress(Math.round((i / Math.max(newFiles.length, 1)) * 100));
        try {
          compressed.push(await compressImageFile(newFiles[i], {
            maxWidth: 2560, maxHeight: 2560, quality: 0.85, maxSizeMB: 5, outputFormat: 'image/webp',
          }));
        } catch {
          compressed.push(newFiles[i]);
        }
      }

      setUploadStage('uploading');
      setUploadProgress(0);
      const intervalMs = Math.max(50, Math.round((compressed.length + activeExisting.length) * 37));
      let sim = 0;
      simInterval = setInterval(() => {
        sim = Math.min(sim + 2, 90);
        setUploadProgress(sim);
      }, intervalMs);
      simulationRef.current = simInterval;

      const removeMediaIds = existingPhotos.filter(p => p.removed).map(p => p.mediaId);
      const finalCoverId = typeof coverMediaId === 'number' && coverMediaId > 0 ? coverMediaId : null;

      const updated = await wallPostsApi.updatePost(post.id, {
        content,
        newImages: compressed.length > 0 ? compressed : undefined,
        removeMediaIds: removeMediaIds.length > 0 ? removeMediaIds : undefined,
        coverMediaId: finalCoverId,
      });

      clearInterval(simInterval);
      simulationRef.current = null;

      // Быстрый доезд до 100%
      let finish = sim;
      const finishInterval = setInterval(() => {
        finish = Math.min(finish + 5, 100);
        setUploadProgress(finish);
        if (finish >= 100) clearInterval(finishInterval);
      }, 30);

      setTimeout(() => {
        onUpdated(updated);
        onClose();
      }, 350);
    } catch (e: any) {
      if (simInterval) clearInterval(simInterval);
      setError(e?.response?.data?.error ?? e?.message ?? 'Ошибка при сохранении');
    } finally {
      if (simulationRef.current) { clearInterval(simulationRef.current); simulationRef.current = null; }
      setIsSubmitting(false);
    }
  };

  return (
    <BottomSheet isOpen={isOpen} onClose={onClose} title="Редактировать пост" size="large">
      <div className={styles.root}>
        {/* Прогресс загрузки */}
        {isSubmitting && (
          <div className={styles.progressOverlay}>
            <div className={styles.progressBox}>
              <i className="ri-loader-4-line ri-spin" />
              <span>{uploadStage === 'compressing' ? 'Сжатие фото...' : 'Сохранение...'}</span>
              {uploadStage === 'uploading' && (
                <>
                  <div className={styles.progressBar}>
                    <div className={styles.progressFill} style={{ width: `${uploadProgress}%` }} />
                  </div>
                  <span className={styles.progressPct}>{uploadProgress}%</span>
                </>
              )}
            </div>
          </div>
        )}

        {error && (
          <div className={styles.error}>
            <i className="ri-error-warning-line" />
            <span>{error}</span>
            <button onClick={() => setError(null)}><i className="ri-close-line" /></button>
          </div>
        )}

        <textarea
          className={styles.textarea}
          value={content}
          onChange={e => setContent(e.target.value)}
          rows={4}
          placeholder="Текст поста..."
          disabled={isSubmitting}
        />

        {/* Фото */}
        {(activeExisting.length > 0 || newFiles.length > 0) && (
          <div className={styles.photoGrid}>
            {/* Существующие фото */}
            {existingPhotos.filter(p => !p.removed).map(p => (
              <div
                key={p.mediaId}
                className={`${styles.photoItem} ${coverMediaId === p.mediaId ? styles.isCover : ''}`}
              >
                <img src={p.thumbUrl} alt="" />
                {coverMediaId === p.mediaId && (
                  <div className={styles.coverBadge}><i className="ri-star-fill" /> Главное</div>
                )}
                <div className={styles.photoActions}>
                  {coverMediaId !== p.mediaId && (
                    <button
                      className={styles.actionBtn}
                      title="Сделать главным"
                      onClick={() => setCoverMediaId(p.mediaId)}
                    >
                      <i className="ri-star-line" />
                    </button>
                  )}
                  <button
                    className={`${styles.actionBtn} ${styles.removeBtn}`}
                    title="Удалить"
                    onClick={() => handleRemoveExisting(p.mediaId)}
                  >
                    <i className="ri-close-line" />
                  </button>
                </div>
              </div>
            ))}

            {/* Новые фото */}
            {newPreviews.map((url, i) => (
              <div key={`new-${i}`} className={styles.photoItem}>
                <img src={url} alt="" />
                <div className={styles.newBadge}>Новое</div>
                <div className={styles.photoActions}>
                  <button
                    className={`${styles.actionBtn} ${styles.removeBtn}`}
                    title="Удалить"
                    onClick={() => handleRemoveNew(i)}
                  >
                    <i className="ri-close-line" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Добавить фото */}
        {canAddMore && (
          <button
            className={styles.addPhotoBtn}
            onClick={() => fileInputRef.current?.click()}
            disabled={isSubmitting}
          >
            <i className="ri-image-add-line" />
            Добавить фото ({totalPhotos}/{MAX_PHOTOS})
          </button>
        )}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          style={{ display: 'none' }}
          onChange={handleFileSelect}
        />

        <div className={styles.buttons}>
          <StatusActionButton variant="secondary" onClick={onClose} disabled={isSubmitting} size="small">
            Отмена
          </StatusActionButton>
          <StatusActionButton
            variant="primary"
            onClick={handleSubmit}
            disabled={isSubmitting || (!content.trim() && totalPhotos === 0)}
            loading={isSubmitting}
            size="small"
          >
            {isSubmitting ? 'Сохранение...' : 'Сохранить'}
          </StatusActionButton>
        </div>
      </div>
    </BottomSheet>
  );
};

export default EditPostModal;
